import { useState, useRef } from 'react';
import { Upload, Download, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  parseIncomeStatementCsv,
  parseBalanceSheetCsv,
  parseCashFlowCsv,
  generateIncomeStatementTemplate,
  generateBalanceSheetTemplate,
  generateCashFlowTemplate,
} from '@/lib/csvImport';
import type { CsvImportData } from '@/types/stock';

interface Props {
  open: boolean;
  onClose: () => void;
  onImport: (data: CsvImportData) => void;
}

type CsvType = 'income' | 'balance' | 'cashflow';

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function CsvUploadDialog({ open, onClose, onImport }: Props) {
  const [parsedIncome, setParsedIncome] = useState<CsvImportData['incomeStatements'] | null>(null);
  const [parsedBalance, setParsedBalance] = useState<CsvImportData['balanceSheets'] | null>(null);
  const [parsedCashFlow, setParsedCashFlow] = useState<CsvImportData['cashFlows'] | null>(null);
  const [errors, setErrors] = useState<Partial<Record<CsvType, string>>>({});

  const incomeRef = useRef<HTMLInputElement>(null);
  const balanceRef = useRef<HTMLInputElement>(null);
  const cashflowRef = useRef<HTMLInputElement>(null);

  function handleFile(type: CsvType, file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      try {
        if (type === 'income') {
          const parsed = parseIncomeStatementCsv(text);
          if (parsed.length === 0) throw new Error('No valid rows found');
          setParsedIncome(parsed);
          setErrors((prev) => ({ ...prev, income: undefined }));
        } else if (type === 'balance') {
          const parsed = parseBalanceSheetCsv(text);
          if (parsed.length === 0) throw new Error('No valid rows found');
          setParsedBalance(parsed);
          setErrors((prev) => ({ ...prev, balance: undefined }));
        } else {
          const parsed = parseCashFlowCsv(text);
          if (parsed.length === 0) throw new Error('No valid rows found');
          setParsedCashFlow(parsed);
          setErrors((prev) => ({ ...prev, cashflow: undefined }));
        }
      } catch (err) {
        setErrors((prev) => ({ ...prev, [type]: (err as Error).message }));
      }
    };
    reader.readAsText(file);
  }

  function handleImport() {
    if (!parsedIncome || !parsedBalance || !parsedCashFlow) return;
    onImport({
      incomeStatements: parsedIncome,
      balanceSheets: parsedBalance,
      cashFlows: parsedCashFlow,
    });
    onClose();
  }

  const canImport = parsedIncome && parsedBalance && parsedCashFlow;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Financial Data from CSV</DialogTitle>
          <DialogDescription>
            Upload CSV files for Income Statement, Balance Sheet, and Cash Flow Statement.
            Download the templates below to see the expected format.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="income">
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="income" className="flex items-center gap-1.5">
              {parsedIncome ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : null}
              Income
            </TabsTrigger>
            <TabsTrigger value="balance" className="flex items-center gap-1.5">
              {parsedBalance ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : null}
              Balance Sheet
            </TabsTrigger>
            <TabsTrigger value="cashflow" className="flex items-center gap-1.5">
              {parsedCashFlow ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : null}
              Cash Flow
            </TabsTrigger>
          </TabsList>

          {/* Income Statement Tab */}
          <TabsContent value="income" className="space-y-3 mt-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadCsv(generateIncomeStatementTemplate(), 'income_template.csv')}>
                <Download className="w-3.5 h-3.5 mr-1" /> Template
              </Button>
              <Button variant="outline" size="sm" onClick={() => incomeRef.current?.click()}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload Income CSV
              </Button>
              <input ref={incomeRef} type="file" accept=".csv" className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile('income', e.target.files[0])} />
            </div>
            {errors.income && (
              <Alert variant="destructive"><AlertCircle className="w-4 h-4" /><AlertDescription>{errors.income}</AlertDescription></Alert>
            )}
            {parsedIncome && (
              <Alert><CheckCircle2 className="w-4 h-4" /><AlertDescription>{parsedIncome.length} years loaded ({parsedIncome[0]?.fiscalYear}–{parsedIncome[parsedIncome.length - 1]?.fiscalYear})</AlertDescription></Alert>
            )}
            <p className="text-xs text-muted-foreground">
              Required columns: <code>fiscal_year, revenue, gross_profit, operating_income, net_income, eps</code>
            </p>
          </TabsContent>

          {/* Balance Sheet Tab */}
          <TabsContent value="balance" className="space-y-3 mt-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadCsv(generateBalanceSheetTemplate(), 'balance_template.csv')}>
                <Download className="w-3.5 h-3.5 mr-1" /> Template
              </Button>
              <Button variant="outline" size="sm" onClick={() => balanceRef.current?.click()}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload Balance CSV
              </Button>
              <input ref={balanceRef} type="file" accept=".csv" className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile('balance', e.target.files[0])} />
            </div>
            {errors.balance && (
              <Alert variant="destructive"><AlertCircle className="w-4 h-4" /><AlertDescription>{errors.balance}</AlertDescription></Alert>
            )}
            {parsedBalance && (
              <Alert><CheckCircle2 className="w-4 h-4" /><AlertDescription>{parsedBalance.length} years loaded</AlertDescription></Alert>
            )}
            <p className="text-xs text-muted-foreground">
              Required columns: <code>fiscal_year, current_assets, current_liabilities, total_assets, total_liabilities, total_equity, long_term_debt, cash</code>
            </p>
          </TabsContent>

          {/* Cash Flow Tab */}
          <TabsContent value="cashflow" className="space-y-3 mt-4">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadCsv(generateCashFlowTemplate(), 'cashflow_template.csv')}>
                <Download className="w-3.5 h-3.5 mr-1" /> Template
              </Button>
              <Button variant="outline" size="sm" onClick={() => cashflowRef.current?.click()}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload Cash Flow CSV
              </Button>
              <input ref={cashflowRef} type="file" accept=".csv" className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile('cashflow', e.target.files[0])} />
            </div>
            {errors.cashflow && (
              <Alert variant="destructive"><AlertCircle className="w-4 h-4" /><AlertDescription>{errors.cashflow}</AlertDescription></Alert>
            )}
            {parsedCashFlow && (
              <Alert><CheckCircle2 className="w-4 h-4" /><AlertDescription>{parsedCashFlow.length} years loaded</AlertDescription></Alert>
            )}
            <p className="text-xs text-muted-foreground">
              Required columns: <code>fiscal_year, operating_cash_flow, capital_expenditures, free_cash_flow</code>
            </p>
          </TabsContent>
        </Tabs>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            {canImport ? '✓ All 3 files ready to import' : `${[parsedIncome, parsedBalance, parsedCashFlow].filter(Boolean).length}/3 files uploaded`}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleImport} disabled={!canImport}>
              Import Data
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
